/**
 * Revenue configuration.
 *
 * How an aggregator front-end earns: every major routing API accepts a partner/referrer
 * address plus a fee in basis points, takes that cut out of each swap at settlement, and
 * sends it to the address given. No contract of our own, no LP capital, no
 * impermanent loss - the fee rides along with a trade that was going to happen anyway.
 *
 * That is the whole mechanism, and it is why the aggregator model can be profitable
 * without owning liquidity. Every provider in ./providers translates this one config
 * into its own API's parameter names.
 *
 * Deliberately OFF by default. Charging users money is a business decision with tax,
 * disclosure and terms-of-service consequences, so it requires two explicit env vars
 * rather than defaulting to some plausible number.
 */

/**
 * Hard ceiling on the configurable fee, as a safety rail rather than a policy.
 * Typical aggregator front-ends charge somewhere in the 10-100 bps range. This cap
 * exists so a misplaced decimal in an env var cannot silently charge 30% of a trade.
 */
export const MAX_FEE_BPS = 300

const ADDRESS_PATTERN = /^0x[0-9a-fA-F]{40}$/

export interface RevenueConfig {
  enabled: boolean
  /** Basis points taken from each swap. 25 = 0.25%. */
  feeBps: number
  /** Address that receives the fee. */
  feeRecipient: string
  /** Set when the configuration was rejected, so it can be logged loudly. */
  configError?: string
}

const DISABLED: RevenueConfig = { enabled: false, feeBps: 0, feeRecipient: '' }

export function parseRevenueConfig(
  rawBps: string | undefined,
  rawRecipient: string | undefined
): RevenueConfig {
  // Neither set: fees are simply off. Not an error.
  if (!rawBps && !rawRecipient) return DISABLED

  // One set without the other is a misconfiguration worth reporting, because the
  // operator clearly intended to charge a fee and it silently would not apply.
  if (!rawBps || !rawRecipient) {
    return {
      ...DISABLED,
      configError:
        'Both REACT_APP_FEE_BPS and REACT_APP_FEE_RECIPIENT must be set for swap fees to apply. Fees are off.',
    }
  }

  const feeBps = Number(rawBps)

  if (!Number.isFinite(feeBps) || !Number.isInteger(feeBps) || feeBps < 0) {
    return { ...DISABLED, configError: `REACT_APP_FEE_BPS must be a non-negative integer, got "${rawBps}". Fees are off.` }
  }
  if (feeBps > MAX_FEE_BPS) {
    return {
      ...DISABLED,
      configError: `REACT_APP_FEE_BPS of ${feeBps} exceeds the ${MAX_FEE_BPS} bps safety cap. Fees are off.`,
    }
  }
  if (!ADDRESS_PATTERN.test(rawRecipient)) {
    return { ...DISABLED, configError: `REACT_APP_FEE_RECIPIENT is not a valid address. Fees are off.` }
  }
  // A zero fee with a valid recipient is a legitimate "configured but not charging" state.
  if (feeBps === 0) return { ...DISABLED, feeRecipient: rawRecipient }

  return { enabled: true, feeBps, feeRecipient: rawRecipient }
}

export const revenueConfig: RevenueConfig = parseRevenueConfig(
  process.env.REACT_APP_FEE_BPS,
  process.env.REACT_APP_FEE_RECIPIENT
)

if (revenueConfig.configError) {
  // eslint-disable-next-line no-console
  console.warn(`[revenue] ${revenueConfig.configError}`)
}

/** Fee as a decimal fraction, e.g. 25 bps -> 0.0025. Some APIs want it in this form. */
export function feeAsFraction(config: RevenueConfig = revenueConfig): number {
  return config.enabled ? config.feeBps / 10_000 : 0
}

/** Fee as a percentage, e.g. 25 bps -> 0.25. Other APIs want it in *this* form. */
export function feeAsPercent(config: RevenueConfig = revenueConfig): number {
  return config.enabled ? config.feeBps / 100 : 0
}
